import { useEffect, useState } from 'react';

// Soft, ambient time labels — "just now", "a moment ago", "5 min ago".
// The PRD calls for relative timestamps on the card front (PRD §2 "Card
// Anatomy") to communicate freshness without ever exposing a hard time
// value. Re-renders every 30s so the label drifts naturally without ever
// looking stale.

const TICK_INTERVAL_MS = 30 * 1000;

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return 'just now';

  const evaluatedAt = Date.parse(iso);
  if (Number.isNaN(evaluatedAt)) return 'just now';

  const diffSeconds = Math.max(0, Math.floor((Date.now() - evaluatedAt) / 1000));

  if (diffSeconds < 30) return 'just now';
  if (diffSeconds < 90) return 'a moment ago';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

export const useRelativeTime = (iso: string | null | undefined): string => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return formatRelative(iso);
};
